import { isDate } from 'date-fns/isDate';
import { isEqual } from 'date-fns/isEqual';
import { DateFieldMinuteStep, DateFieldView } from '../../../theming';
import { convertTo12Hour, isDateValid, rangeArray } from '../../../utils';
import { convertTo24Hour } from '../../../utils/convertTo24Hour';
import { formatDate, formatDateTime, formatTime } from '../../../utils/formatDate';
import {
    DateFieldOption,
    DateFieldSection,
    DateFieldSectionBoundaries,
    DateFieldSectionValue,
    Direction,
} from './useDateField.types';
import { getLocalisedMeridiemOptions } from './getLocalisedMeridiemOptions';

const getTimeRange = (view: 'hours12' | 'hours24' | 'minutes' | 'seconds', step = 1): Array<string> => {
    if (['minutes', 'seconds'].includes(view)) {
        return rangeArray(0, 59, step);
    }

    if (['hours24'].includes(view)) {
        return rangeArray(0, 23, step);
    }

    if (['hours12'].includes(view)) {
        const array = rangeArray(1, 12, step);
        const last = array.pop();
        array.unshift(last as string);
        return array;
    }

    return [];
};

export const getInitialState = (
    value: Date | null | undefined,
    views: Array<DateFieldView>,
    minuteStep: DateFieldMinuteStep,
    locale: string
): Array<DateFieldSection> => {
    // Return the editable date "sections" in the correct order.
    const day = value ? value.getDate() : null;
    const month = value ? value.getMonth() + 1 : null;
    const year = value ? value.getFullYear() : null;

    const hours24 = value ? value.getHours() : null;
    const minutes = value ? value.getMinutes() : null;
    const seconds = value ? value.getSeconds() : null;

    const hours12 = hours24 !== null ? convertTo12Hour(hours24) : null;
    const meridiem = hours24 !== null ? (hours24 >= 12 ? 'PM' : 'AM') : null;

    const includesDay = views.includes('day');
    const includesMonth = views.includes('month');
    const includesYear = views.includes('year');

    const includeHours = views.includes('hours');
    const includeMinutes = views.includes('minutes');
    const includeMeridiem = views.includes('meridiem');
    const includeSeconds = views.includes('seconds');

    const sections: Array<DateFieldSection> = [];

    let startPos = 0;
    let endPos = 0;

    // Day
    if (includesDay) {
        endPos = startPos + 2;
        sections.push({
            key: 'day',
            value: day,
            boundaries: {
                upper: 31,
                lower: 1,
            },
            options: [],
            position: {
                start: startPos,
                end: endPos,
            },
            placeholder: 'dd',
            contentType: 'digits',
            valid: day !== null,
        });
        startPos = endPos + 1;
    }

    // Month
    if (includesMonth) {
        endPos = startPos + 2;
        sections.push({
            key: 'month',
            value: month,
            boundaries: {
                upper: 12,
                lower: 1,
            },
            options: [],
            position: {
                start: startPos,
                end: endPos,
            },
            placeholder: 'MM',
            contentType: 'digits',
            valid: month !== null,
        });
        startPos = endPos + 1;
    }

    // Year
    if (includesYear) {
        endPos = startPos + 4;
        sections.push({
            key: 'year',
            value: year,
            defaultValue: new Date().getFullYear(),
            options: [],
            position: {
                start: startPos,
                end: endPos,
            },
            placeholder: 'YYYY',
            contentType: 'digits',
            valid: year !== null,
        });
        startPos = endPos + 1;
    }

    // Hours
    if (includeHours) {
        endPos = startPos + 2;
        sections.push({
            key: 'hours',
            value: includeMeridiem ? hours12 : hours24,
            boundaries: {
                upper: includeMeridiem ? 12 : 23,
                lower: includeMeridiem ? 1 : 0,
            },
            options: getTimeRange(includeMeridiem ? 'hours12' : 'hours24').map((t) => {
                return { value: t };
            }),
            position: {
                start: startPos,
                end: endPos,
            },
            placeholder: 'hh',
            contentType: 'digits',
            valid: hours24 !== null,
        });
        startPos = endPos + 1;
    }

    // Minutes
    if (includeMinutes) {
        endPos = startPos + 2;
        sections.push({
            key: 'minutes',
            value: minutes,
            contentType: 'digits',
            boundaries: {
                upper: 59,
                lower: 0,
            },
            options: getTimeRange('minutes', minuteStep).map((t) => {
                return { value: t };
            }),
            position: {
                start: startPos,
                end: endPos,
            },
            placeholder: 'mm',
            valid: minutes !== null,
        });
        startPos = endPos + 1;
    }

    // Seconds
    if (includeSeconds) {
        endPos = startPos + 2;
        sections.push({
            key: 'seconds',
            value: seconds,
            contentType: 'digits',
            boundaries: {
                upper: 60,
                lower: 0,
            },
            options: getTimeRange('seconds').map((t) => {
                return { value: t };
            }),
            position: {
                start: startPos,
                end: endPos,
            },
            placeholder: 'ss',
            valid: seconds !== null,
        });
        startPos = endPos + 1;
    }

    // Meridiem
    if (includeMeridiem) {
        endPos = startPos + 2;
        sections.push({
            key: 'meridiem',
            value: meridiem,
            contentType: 'letters',
            options: getLocalisedMeridiemOptions(locale),
            position: {
                start: startPos,
                end: endPos,
            },
            placeholder: 'aa',
            valid: seconds !== null,
        });
        startPos = endPos + 1;
    }

    return sections;
};

export const getSectionFromKey = (sections: Array<DateFieldSection>, key: DateFieldView): DateFieldSection | null => {
    return sections.find((sec) => sec.key === key) || null;
};

export const getSectionValue = (sections: Array<DateFieldSection>, key: DateFieldView): number | string | null => {
    const section = getSectionFromKey(sections, key);
    return section ? section.value : null;
};

export const composeDateFromState = (sections: Array<DateFieldSection>, referenceDate?: Date | null): Date | null => {
    const day = getSectionFromKey(sections, 'day');
    const month = getSectionFromKey(sections, 'month');
    const year = getSectionFromKey(sections, 'year');

    const hours = getSectionFromKey(sections, 'hours');
    const minutes = getSectionFromKey(sections, 'minutes');
    const seconds = getSectionFromKey(sections, 'seconds');
    const meridiem = getSectionFromKey(sections, 'meridiem');

    // Day is mandatory only if the section exists
    if (day && day.value == null) {
        return null;
    }

    // Month is mandatory only if the section exists
    if (month && month.value == null) {
        return null;
    }

    // Year is mandatory only if the section exists
    if (year && year.value == null) {
        return null;
    }

    // Hours are mandatory
    if (hours && hours.value == null) {
        return null;
    }

    // Minutes are mandatory
    if (minutes && minutes.value == null) {
        return null;
    }

    // Seconds are mandatory only if the section exists
    if (seconds && seconds.value == null) {
        return null;
    }

    // Meridiem is mandatory only if the section exists
    if (meridiem && meridiem.value == null) {
        return null;
    }

    // If we have a reference date then we compose a new date based on that, otherwise we use a new date.
    const newDate = referenceDate ? new Date(referenceDate?.getTime()) : new Date();

    // Check that this combination of year month and day are valid.
    const dateValid = isDateValid(
        year ? (year.value as number) : newDate.getFullYear(),
        month ? (month.value as number) - 1 : newDate.getMonth(),
        day ? (day.value as number) : newDate.getDate()
    );

    if (!dateValid) {
        return null;
    }

    if (day) {
        newDate.setDate(day.value as number);
    }

    if (month) {
        newDate.setMonth((month.value as number) - 1);
    }

    if (year) {
        newDate.setFullYear(year.value as number);
    }

    if (hours) {
        let hoursValue = hours.value as number;
        if (meridiem) {
            hoursValue = convertTo24Hour(hoursValue, meridiem.value as 'AM' | 'PM') ?? 0;
        }
        newDate.setHours(hoursValue as number);
    }

    if (minutes) {
        newDate.setMinutes(minutes.value as number);
    }

    newDate.setSeconds((seconds?.value as number) ?? 0);
    newDate.setMilliseconds(0);

    return newDate;
};

export const getSelectedSectionIndex = (sections: Array<DateFieldSection>, key: DateFieldView): number => {
    return sections.findIndex((sec) => sec.key === key);
};

export const getSectionValueString = (section: DateFieldSection): string => {
    const { value, placeholder, position } = section;
    const length = position.end - position.start;
    return value == null ? placeholder : value.toString().padStart(length, '0');
};

export const getMaskValueFromSectionState = (sections: Array<DateFieldSection>): string => {
    const day = getSectionFromKey(sections, 'day');
    const month = getSectionFromKey(sections, 'month');
    const year = getSectionFromKey(sections, 'year');

    const hours = getSectionFromKey(sections, 'hours');
    const minutes = getSectionFromKey(sections, 'minutes');
    const seconds = getSectionFromKey(sections, 'seconds');
    const meridiem = getSectionFromKey(sections, 'meridiem');

    let date = '';
    if (day) {
        const dayStr = day ? getSectionValueString(day) : '';
        date = dayStr;
    }

    if (month) {
        const monthStr = month ? getSectionValueString(month) : '';
        date = `${date}${date && '/'}${monthStr}`;
    }

    if (year) {
        const yearStr = year ? getSectionValueString(year) : '';
        date = `${date}${date && '/'}${yearStr}`;
    }

    let time = '';
    if (hours) {
        const hoursStr = hours ? getSectionValueString(hours) : '';
        time = hoursStr;
    }

    if (minutes) {
        const minutesStr = minutes ? getSectionValueString(minutes) : '';
        time = `${time}${time && ':'}${minutesStr}`;
    }

    if (seconds) {
        const secondsStr = seconds ? getSectionValueString(seconds) : '';
        time = `${time}${time && ':'}${secondsStr}`;
    }

    if (meridiem) {
        time = `${time} ${
            meridiem.value == null
                ? meridiem.placeholder
                : meridiem.options.find((o) => o.value === meridiem.value)?.text
        }`;
    }

    if (date && time) {
        // combine date and time
        return `${date} ${time}`;
    }

    return date || time;
};

export const bumpSectionDigitsValue = (value: number | null, defaultValue: number, dir: 'up' | 'down'): number => {
    // Increase or decrease the current value.
    if (value == null) {
        // No current value, so return the default value
        return defaultValue;
    }
    return value + (dir === 'up' ? 1 : -1);
};

export const bumpSectionDigitsValueWithBoundaries = (
    value: number | null,
    boundaries: DateFieldSectionBoundaries,
    dir: 'up' | 'down'
): number => {
    // Increase or decrease the current value according to the defined boundaries.
    const { lower, upper } = boundaries;

    if (value == null) {
        // No current value, so return the lower or upper boundary.
        return dir === 'up' ? lower : upper;
    }
    if (dir === 'up' && value === upper) {
        return lower;
    }
    if (dir === 'down' && value === lower) {
        return upper;
    }
    return value + (dir === 'up' ? 1 : -1);
};

export const bumpSectionLettersValue = (
    value: string | null,
    options: Array<DateFieldOption>,
    dir: 'up' | 'down'
): string => {
    // Select the previous or next option
    const currentIndex = options.findIndex((option) => option.value === value);

    if (value == null || currentIndex === -1) {
        // No current value, so return the first or last option.
        return dir === 'up' ? options[0].value : options[options.length - 1].value;
    }

    let nextIndex = currentIndex + (dir === 'up' ? 1 : -1);
    const maxIndex = options.length - 1;

    if (dir === 'up' && nextIndex > maxIndex) {
        nextIndex = 0;
    }

    if (dir === 'down' && nextIndex < 0) {
        nextIndex = maxIndex;
    }

    return options[nextIndex].value;
};

export const bumpSectionValue = (section: DateFieldSection, dir: 'up' | 'down'): DateFieldSectionValue => {
    const { value, contentType, boundaries, options, defaultValue } = section;

    if (contentType === 'digits') {
        if (typeof value !== 'string') {
            if (defaultValue) {
                return bumpSectionDigitsValue(value, defaultValue, dir);
            }
            if (boundaries) {
                return bumpSectionDigitsValueWithBoundaries(value, boundaries, dir);
            }
        }
    }

    if (contentType === 'letters') {
        if (typeof value !== 'number' && options) {
            return bumpSectionLettersValue(value, options, dir);
        }
    }

    return value;
};

export const getDefaultSectionValue = (section: DateFieldSection): DateFieldSectionValue => {
    const { contentType, boundaries, options } = section;
    return contentType === 'digits' ? boundaries?.lower ?? null : options?.[0].value ?? null;
};

export const areAllSectionsEmpty = (sections: Array<DateFieldSection>): boolean => {
    if (sections.some((section) => section.valid)) {
        return false;
    }
    return true;
};

export const areDateValuesEqual = (firstDate: Date | null, secondDate: Date | null) => {
    if (isDate(firstDate) && isDate(secondDate)) {
        return isEqual(firstDate as Date, secondDate as Date);
    }
    // One or both of the values are null
    return Boolean(firstDate) === Boolean(secondDate);
};

export const getSectionOptions = (section: DateFieldSection): Array<{ value: string | number; text: string }> => {
    const { options, contentType } = section;

    return options.map((option) => {
        const value = contentType === 'digits' ? parseInt(option.value) : option.value;
        return { value, text: option.text || option.value };
    });
};

export const getNextSectionKey = (
    activeSectionKey: DateFieldView | null,
    sections: Array<DateFieldSection>,
    dir: Direction
): DateFieldView | null => {
    if (dir === 'first') {
        return sections[0].key;
    }

    if (dir === 'last') {
        return sections[sections.length - 1].key;
    }

    if (!activeSectionKey) {
        return null;
    }

    const currentIndex = getSelectedSectionIndex(sections, activeSectionKey);

    if (dir === 'prev' && currentIndex === 0) {
        return null;
    }
    if (dir === 'next' && currentIndex === sections.length - 1) {
        return null;
    }

    const targetIndex = currentIndex + (dir === 'next' ? 1 : -1);

    return sections[targetIndex].key;
};

export const viewsIncludeDateOrTime = (views: Array<DateFieldView>): Array<'date' | 'time'> => {
    const includes: Array<'date' | 'time'> = [];
    if (views.some((view) => ['day', 'month', 'year'].includes(view))) {
        includes.push('date');
    }
    if (views.some((view) => ['hours', 'minutes', 'seconds', 'meridiem'].includes(view))) {
        includes.push('time');
    }
    return includes;
};

export const getFormattedValue = (value: Date, views: Array<DateFieldView>, locale?: string): string | null => {
    const includes = viewsIncludeDateOrTime(views);
    const includesDate = includes.includes('date');
    const includesTime = includes.includes('time');

    const withMeridiam = views.includes('meridiem');
    const hourCycle = withMeridiam ? 'h12' : 'h23';
    const options: Intl.DateTimeFormatOptions = { hourCycle };

    if (includesDate && includesTime) {
        return formatDateTime({ locale, date: value, options });
    }
    if (includesDate) {
        return formatDate({ locale, date: value, options });
    }
    if (includesTime) {
        return formatTime({ locale, date: value, options });
    }

    return null;
};
